import React, { useEffect, useState, useRef } from 'react';
import { type ModuleData, getFamiliesForModule } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { WorldTile } from './WorldTile';
import { hasActiveDecay } from '../../mastery/masteryDecay';
import { getFamilyMasteryKey } from '../../mastery';
import type { FamilyMasteryEntry } from '../../mastery/types';
interface ZoneCarouselProps {
  modules: ModuleData[];
  selectedGroup: GroupId;
  familyMastery: Record<string, FamilyMasteryEntry> | undefined;
  getModuleMasteryForGroup: (moduleId: string) => number;
  getAnsweredQuestions: (moduleId: string) => unknown[];
  onModuleSelect: (module: ModuleData) => void;
}
/**
 * ZoneCarousel — responsive container for a list of WorldTiles.
 *
 * - <lg (mobile + tablet portrait): horizontal scroll-snap carousel with edge peek
 *   and pagination dots. Native CSS scroll-snap, no third-party library.
 * - lg+ (tablet landscape, Chromebook, desktop): traditional responsive grid.
 *
 * The breakpoint is `lg` (1024px) so tablet portrait also gets the swipe UX,
 * and Chromebooks (1366×768) get the grid for at-a-glance comparison.
 *
 * F5.4 — Mobile experience pilot.
 */
export function ZoneCarousel({
  modules,
  selectedGroup,
  familyMastery,
  getModuleMasteryForGroup,
  getAnsweredQuestions,
  onModuleSelect
}: ZoneCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // Track which tile is most-visible in the carousel (mobile only).
  // Uses IntersectionObserver — no scroll-listener spam, GPU-friendly.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || modules.length <= 1) return;
    // IntersectionObserver fires when scrolled past breakpoints.
    // We pick the highest intersectionRatio to set active dot.
    const visibility = new Map<number, number>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const idxAttr = (entry.target as HTMLElement).dataset.idx;
        if (!idxAttr) return;
        const idx = Number(idxAttr);
        visibility.set(idx, entry.intersectionRatio);
      });
      // Find the tile with the highest visibility
      let bestIdx = 0;
      let bestRatio = 0;
      visibility.forEach((ratio, idx) => {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestIdx = idx;
        }
      });
      setActiveIndex(bestIdx);
    }, {
      root: scroller,
      threshold: [0.25, 0.5, 0.75, 1]
    });
    itemRefs.current.forEach(el => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [modules.length]);
  const decayCheck = (module: ModuleData): boolean => {
    const families = getFamiliesForModule(module.id);
    return families.some(f => {
      const key = getFamilyMasteryKey(f.familyId, selectedGroup);
      return hasActiveDecay(familyMastery?.[key]);
    });
  };
  return <div data-id="element-2512">
      {/* Tile list — flex carousel <lg, grid lg+ */}
      <div ref={scrollerRef} className="
          flex lg:grid
          lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5
          gap-4 sm:gap-5 lg:gap-6
          overflow-x-auto lg:overflow-visible
          snap-x snap-mandatory lg:snap-none
          scrollbar-hide
          -mx-4 sm:-mx-6 lg:mx-0
          px-4 sm:px-6 lg:px-0
          pb-2 lg:pb-0
          lg:justify-items-center
        " style={{
      // Smooth momentum on iOS; ignored on desktop
      WebkitOverflowScrolling: 'touch'
    }} role="region" aria-label="Module-keuze" data-id="element-2513">
        {modules.map((module, index) => <div key={module.id} ref={el => {
        itemRefs.current[index] = el;
      }} data-idx={index} className="
              shrink-0 lg:shrink
              basis-[44%] sm:basis-[32%] lg:basis-auto
              snap-center lg:snap-align-none
              flex justify-center
              lg:w-full
            " data-id="element-2514">
            <WorldTile module={module} selectedGroup={selectedGroup} onClick={() => onModuleSelect(module)} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} index={index} hasDecay={decayCheck(module)} data-id="element-2515" />
          </div>)}
      </div>

      {/* Pagination dots — mobile/tablet portrait only */}
      {modules.length > 1 && <div className="lg:hidden flex justify-center gap-1.5 mt-4" role="tablist" aria-label="Module-positie" data-id="element-2516">
          {modules.map((module, i) => {
        const isActive = i === activeIndex;
        return <button key={module.id} type="button" role="tab" aria-selected={isActive} aria-label={`Ga naar ${module.title}`} onClick={() => {
          itemRefs.current[i]?.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest'
          });
        }} className={`
                  h-2 rounded-full transition-all duration-300
                  ${isActive ? 'w-6 bg-slate-700' : 'w-2 bg-slate-300 hover:bg-slate-400'}
                `} data-id="element-2517" />;
      })}
        </div>}
    </div>;
}